import { api } from './api';
import { ReportSection, ReportSectionId, ReportTemplate } from '../components/report-builder/types';

// Async fire-and-forget responses
interface AsyncAcceptedResponse {
  success: boolean;
  requestId: string;
  status: 'accepted';
}

interface GenerateSectionAcceptedResponse extends AsyncAcceptedResponse {
  sectionId: ReportSectionId;
}

// Report state includes sections + async status
export interface ReportStatus {
  readiness: 'running' | 'complete' | 'failed' | null;
  readinessError: string | null;
  generatingSections: Record<string, { status: string; error: string | null }>;
  exportStatus: 'running' | 'complete' | 'failed' | null;
  exportError: string | null;
  exportDownloadUrl: string | null;
  exportS3Key: string | null;
}

interface ReportStateResponse {
  success: boolean;
  sections: (Omit<ReportSection, 'isGenerating'> & { isGenerating?: boolean })[];
  status: ReportStatus;
}

interface TemplateResponse {
  success: boolean;
  template: ReportTemplate | null;
}

function buildParams(sessionId?: string, scope?: 'session' | 'project') {
  const params: Record<string, string> = {};
  if (sessionId) params.sessionId = sessionId;
  if (scope) params.scope = scope;
  return params;
}

export const reportBuilderService = {
  /**
   * Fire-and-forget: triggers readiness assessment in n8n.
   * Poll getReportState() to get results.
   */
  async assessReadiness(
    projectId: string,
    sessionId?: string,
    scope: 'session' | 'project' = 'project'
  ): Promise<{ requestId: string }> {
    const { data } = await api.post<AsyncAcceptedResponse>(
      `/api/projects/${projectId}/report/assess-readiness`,
      { sessionId, scope }
    );
    return { requestId: data.requestId };
  },

  /**
   * Fire-and-forget: triggers section generation in n8n.
   * Poll getReportState() to get results.
   */
  async generateSection(
    projectId: string,
    sectionId: ReportSectionId,
    sessionId?: string,
    scope: 'session' | 'project' = 'project'
  ): Promise<{ requestId: string; sectionId: ReportSectionId }> {
    const { data } = await api.post<GenerateSectionAcceptedResponse>(
      `/api/projects/${projectId}/report/generate-section`,
      { sectionId, sessionId, scope }
    );
    return { requestId: data.requestId, sectionId: data.sectionId };
  },

  /**
   * Get full report state including sections and async status.
   * This is the polling endpoint.
   */
  async getReportState(
    projectId: string,
    sessionId?: string,
    scope: 'session' | 'project' = 'project'
  ): Promise<{ sections: ReportStateResponse['sections']; status: ReportStatus }> {
    const { data } = await api.get<ReportStateResponse>(
      `/api/projects/${projectId}/report/state`,
      { params: buildParams(sessionId, scope) }
    );
    return { sections: data.sections, status: data.status };
  },

  async saveSection(
    projectId: string,
    sectionId: ReportSectionId,
    content: string,
    sessionId?: string,
    scope: 'session' | 'project' = 'project'
  ): Promise<void> {
    await api.put(`/api/projects/${projectId}/report/sections/${sectionId}`, {
      content,
      sessionId,
      scope,
    });
  },

  /**
   * Fire-and-forget: triggers DOCX export in n8n.
   * Poll getReportState() to get downloadUrl.
   */
  async exportDocx(
    projectId: string,
    clientName?: string,
    sessionId?: string,
    scope: 'session' | 'project' = 'project'
  ): Promise<{ requestId: string }> {
    const { data } = await api.post<AsyncAcceptedResponse>(
      `/api/projects/${projectId}/report/export-docx`,
      { sessionId, scope, clientName }
    );
    return { requestId: data.requestId };
  },

  /**
   * Cancel a stuck readiness assessment or export.
   */
  async cancelOperation(
    projectId: string,
    target: 'readiness' | 'export',
    sessionId?: string,
    scope: 'session' | 'project' = 'project'
  ): Promise<void> {
    await api.post(`/api/projects/${projectId}/report/cancel`, {
      target,
      sessionId,
      scope,
    });
  },

  async uploadTemplate(projectId: string, file: File): Promise<ReportTemplate> {
    const formData = new FormData();
    formData.append('template', file);
    const { data } = await api.post<{ success: boolean; template: ReportTemplate }>(
      `/api/projects/${projectId}/report/template`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data.template;
  },

  async getTemplate(projectId: string): Promise<ReportTemplate | null> {
    const { data } = await api.get<TemplateResponse>(
      `/api/projects/${projectId}/report/template`
    );
    return data.template;
  },
};
